import { layersCut } from '../video/compose';
import { videoExporter } from '../video/engine';
import { exportBytesPerSecond, EXPORT_SPEED, exportNotes } from '../video/plan';
import { getMedia, mediaErrorText } from '../video/media';
import { FRAMES, fmtTime, totalLength, voiceSpans, type Frame, type VideoProject } from '../video/project';
import { thStamp, thTime } from '../time';
import { $, setPressed } from './dom';
import { openSheet } from './sheets';
import { toast } from './toast';
import type { VideoCtx } from './videoView';

/*
 * "ส่งออกวิดีโอ" (board 23): choose 9:16 or 1:1, see the checks, write the MP4 in the browser,
 * then save it to the photo library / share it (iPhone and iPad: the share sheet has "บันทึกวิดีโอ").
 */

const errText = (e: unknown) => e instanceof Error && e.message ? e.message : String(e);

export function initVideoExport(ctx: VideoCtx) {
  const go = $<HTMLButtonElement>('vxGo'), stop = $<HTMLButtonElement>('vxStop'), msg = $('vxMsg');
  let frame: Frame = '9:16';
  let job: AbortController | null = null;
  /** `from`: the work as it was when this file was made (a template's preview must show the work as it is) */
  let result: { blob: Blob; url: string; name: string; from: string } | null = null;

  const say = (text: string) => { msg.textContent = text; msg.hidden = !text; };

  function clearResult() {
    if (result) URL.revokeObjectURL(result.url);
    result = null;
    $('vxDone').hidden = true;
    $<HTMLVideoElement>('vxVideo').removeAttribute('src');
  }

  function paint() {
    const p = ctx.project();
    if (!p) return;
    setPressed('vxFrame', b => b.dataset.v === frame);
    const total = totalLength(p);
    $('vxInfo').textContent = `ยาว ${fmtTime(total)} · MP4 (ภาพ H.264 · เสียง AAC) · ${FRAMES[frame].w} × ${FRAMES[frame].h}`;
    $('vxEst').textContent = total > 0
      ? `ไฟล์ราว ${Math.max(1, Math.round(total * exportBytesPerSecond() / 1048576))} MB · ใช้เวลาราว ${Math.max(2, Math.ceil(total / EXPORT_SPEED))} วิ`
      : '';
    const notes = exportNotes(p, ctx.missing());
    // V2: text/pictures laid out on the work's frame; another export shape fills it from the centre (like the clips)
    const ls = ctx.overlaysShown(), doc = { w: ctx.ed.doc.w, h: ctx.ed.doc.h }, out = FRAMES[frame];
    if (ls.length) {
      const same = doc.w * out.h === doc.h * out.w;
      const cut = same ? 0 : layersCut(ls, doc, out);
      notes.push(cut
        ? { level: 'warn', text: `ข้อความ/รูป ${cut} ชิ้นล้นขอบเมื่อส่งออกเป็น ${frame} (จัดวางไว้บนกรอบ ${doc.w} × ${doc.h}) ส่วนที่ล้นจะถูกตัด` }
        : { level: 'ok', text: `ข้อความ/รูป ${ls.length} ชิ้นอยู่ในกรอบ${same ? '' : ` ${frame}`}` });
    }
    const box = $('vxChecks');
    box.textContent = '';
    for (const n of notes) {
      const item = document.createElement('div'); item.className = 'pfitem';
      const dot = document.createElement('span');
      dot.className = 'pfdot ' + (n.level === 'ok' ? 'ok' : n.level === 'warn' ? 'warn' : 'vstop');
      dot.textContent = n.level === 'ok' ? '✓' : n.level === 'warn' ? '!' : '×';
      const body = document.createElement('div');
      const t = document.createElement('span'); t.textContent = n.text;
      body.append(t);
      item.append(dot, body);
      box.append(item);
    }
    go.disabled = !!job || notes.some(n => n.level === 'stop');
  }

  $('vxFrame').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b || job) return;
    frame = b.dataset.v as Frame;
    clearResult(); paint();
  };

  function open() {
    const p = ctx.project(); if (!p) return;
    if (!job) {
      frame = ctx.ed.doc.w === ctx.ed.doc.h ? '1:1' : '9:16';
      clearResult(); say('');
    }
    paint();
    openSheet('shVExport');
  }

  async function start() {
    const p = ctx.project(); if (!p || job) return;
    const snapshot: VideoProject = JSON.parse(JSON.stringify(p));
    const from = ctx.workJson();
    const keys = [...new Set([...snapshot.clips.map(c => c.media), ...(snapshot.music ? [snapshot.music.media] : [])])];
    clearResult(); say('');
    job = new AbortController();
    const signal = job.signal;
    go.hidden = true; stop.hidden = false;
    $('vxProg').hidden = false;
    $('vxProgTitle').textContent = 'กำลังสร้างวิดีโอ';
    const bar = $('vxBar'), pct = $('vxPct');
    const started = performance.now();
    const show = (x: number) => {
      bar.style.width = `${Math.round(x * 100)}%`;
      const spent = (performance.now() - started) / 1000;
      pct.textContent = x > 0.12 && x < 1 ? `${Math.round(x * 100)}% · เหลือราว ${Math.max(1, Math.round(spent / x - spent))} วิ` : `${Math.round(x * 100)}%`;
    };
    show(0);
    paint();
    let wake: { release: () => Promise<void> } | null = null;
    try { wake = await (navigator as Navigator & { wakeLock?: { request(t: 'screen'): Promise<{ release: () => Promise<void> }> } }).wakeLock?.request('screen') ?? null; } catch { wake = null; }
    try {
      const layers = await ctx.overlays();   // drawn from the work as it is now (throws with a Thai reason)
      const blobs = new Map<string, Blob>();
      for (const k of keys) {
        let m;
        try { m = await getMedia(k); }
        catch (e) { throw new Error('อ่านคลิปจากที่เก็บในเครื่องไม่ได้: ' + mediaErrorText(e)); }
        if (!m) throw new Error('มีคลิปหรือเพลงที่ไม่อยู่ในเครื่องนี้แล้ว ใส่กลับก่อนส่งออก');
        blobs.set(k, m.blob);
      }
      // เสียงพากย์ AI (v4): the voice files are on the server; every one is fetched first, or the reason is shown
      const voices = new Map<string, Blob>();
      for (const v of voiceSpans(snapshot)) {
        if (voices.has(v.audio)) continue;
        let res: Response;
        try { res = await fetch(`/api/voice/${v.audio}/file`, { credentials: 'same-origin', signal }); }
        catch { throw new Error(`โหลดเสียงพากย์ฉากที่ ${v.index + 1} ไม่ได้ (ไม่มีอินเทอร์เน็ต?) ต้องออนไลน์ตอนส่งออกวิดีโอที่มีเสียงพากย์`); }
        if (!res.ok) {
          const why = await res.json().then((b: { error?: string }) => b.error ?? '', () => '');
          throw new Error(`โหลดเสียงพากย์ฉากที่ ${v.index + 1} ไม่ได้ (${res.status}${why ? ' ' + why : ''}) สร้างเสียงใหม่ในหน้า "พากย์"`);
        }
        voices.set(v.audio, await res.blob());
      }
      const blob = await (await videoExporter()).exportVideo(snapshot, FRAMES[frame], k => blobs.get(k)!, show, signal,
        { layers, from: { w: ctx.ed.doc.w, h: ctx.ed.doc.h } }, id => voices.get(id)!);
      const name = `${ctx.ed.doc.name.replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'วิดีโอ'} ${thStamp(new Date())}.mp4`;
      result = { blob, url: URL.createObjectURL(blob), name, from };
      const v = $<HTMLVideoElement>('vxVideo'); v.src = result.url;
      $('vxDoneInfo').textContent = `${name} · ${(blob.size / 1048576).toFixed(1)} MB · ยาว ${fmtTime(totalLength(snapshot))} · สร้างเสร็จ ${thTime(new Date())} (ใช้เวลา ${Math.round((performance.now() - started) / 1000)} วิ)`;
      $('vxSaveNote').textContent = canShare(blob, name) ? 'กดแล้วเลือก "บันทึกวิดีโอ" เพื่อเก็บลงคลังภาพ หรือเลือกแอปที่จะส่งต่อ' : 'กดแล้วไฟล์จะถูกดาวน์โหลดลงเครื่อง';
      $('vxDone').hidden = false;
      $('vxProg').hidden = true;
      toast('สร้างวิดีโอเสร็จแล้ว');
    } catch (e) {
      $('vxProg').hidden = true;
      say(signal.aborted ? 'หยุดสร้างวิดีโอแล้ว ยังไม่มีไฟล์' : errText(e));
      if (!signal.aborted) toast(errText(e), true);
    } finally {
      job = null;
      go.hidden = false; stop.hidden = true;
      try { await wake?.release(); } catch { /* already released */ }
      paint();
    }
  }

  function canShare(blob: Blob, name: string): boolean {
    try { return !!navigator.canShare?.({ files: [new File([blob], name, { type: 'video/mp4' })] }); } catch { return false; }
  }

  $('vxSave').onclick = async () => {
    if (!result) return;
    const file = new File([result.blob], result.name, { type: 'video/mp4' });
    if (canShare(result.blob, result.name)) {
      try { await navigator.share({ files: [file], title: ctx.ed.doc.name }); return; }
      catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') return;   // the person closed the share sheet
        say('เปิดหน้าต่างบันทึก/แชร์ไม่ได้: ' + errText(e) + ' จึงดาวน์โหลดไฟล์แทน');
      }
    }
    const a = document.createElement('a');
    a.href = result.url; a.download = result.name;
    document.body.append(a); a.click(); a.remove();
  };

  go.onclick = () => void start();
  stop.onclick = () => job?.abort();

  return {
    open, paint, get busy() { return !!job; },
    /** the last MP4 of this work, only while the work is still exactly as it was exported */
    get lastFile(): Blob | null { return result && result.from === ctx.workJson() ? result.blob : null; },
    /** another work was opened: drop the finished file of the previous one (it can be ~100 MB) */
    clear() { if (!job) { clearResult(); say(''); } }
  };
}
